import { useCallback, useEffect, useRef, useState } from "react";

let activePricingExitGuard = null;

export const requestActiveUnsavedPricingExit = (action, reason = "route-navigation") => {
  if (typeof activePricingExitGuard === "function") {
    return activePricingExitGuard(action, reason);
  }
  try {
    return Promise.resolve(action?.());
  } catch (error) {
    return Promise.reject(error);
  }
};

export const useUnsavedPricingGuard = ({ isDirty, scopeKey, onDiscard }) => {
  const [confirmation, setConfirmation] = useState(null);
  const dirtyRef = useRef(isDirty);
  const scopeKeyRef = useRef(scopeKey);
  const discardRef = useRef(onDiscard);
  const pendingRef = useRef(null);
  dirtyRef.current = isDirty;
  scopeKeyRef.current = scopeKey;
  discardRef.current = onDiscard;

  const settlePending = useCallback((allowed) => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    setConfirmation(null);
    pending.resolve(allowed);
  }, []);

  const requestExit = useCallback((action, reason = "route-navigation") => {
    if (!dirtyRef.current || !scopeKeyRef.current) {
      try {
        return Promise.resolve(action?.());
      } catch (error) {
        return Promise.reject(error);
      }
    }
    if (pendingRef.current) return pendingRef.current.promise;

    let resolveDecision;
    const promise = new Promise((resolve) => {
      resolveDecision = resolve;
    });
    const pending = {
      action,
      reason,
      scopeKey: scopeKeyRef.current,
      promise,
      resolve: resolveDecision,
    };
    pendingRef.current = pending;
    setConfirmation({ reason, scopeKey: pending.scopeKey });
    return promise;
  }, []);

  const keepEditing = useCallback(() => {
    settlePending(false);
  }, [settlePending]);

  const discardAndContinue = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending) return false;
    if (pending.scopeKey !== scopeKeyRef.current) {
      settlePending(false);
      return false;
    }

    pendingRef.current = null;
    setConfirmation(null);

    let discarded = false;
    try {
      discarded = (await discardRef.current?.(pending.scopeKey)) !== false;
    } catch {
      discarded = false;
    }
    if (!discarded || pending.scopeKey !== scopeKeyRef.current) {
      pending.resolve(false);
      return false;
    }

    try {
      await pending.action?.();
      pending.resolve(true);
      return true;
    } catch (error) {
      pending.resolve(false);
      throw error;
    }
  }, [settlePending]);

  const confirmExit = useCallback(
    (reason = "back") => {
      if (!dirtyRef.current || !scopeKeyRef.current) return true;
      return requestExit(undefined, reason);
    },
    [requestExit]
  );

  useEffect(() => {
    if (!isDirty || !scopeKey) return undefined;
    const registeredGuard = (action, reason) => requestExit(action, reason);
    activePricingExitGuard = registeredGuard;
    return () => {
      if (activePricingExitGuard === registeredGuard) activePricingExitGuard = null;
    };
  }, [isDirty, requestExit, scopeKey]);

  useEffect(() => {
    const pending = pendingRef.current;
    if (pending && (!isDirty || !scopeKey || pending.scopeKey !== scopeKey)) {
      settlePending(false);
    }
  }, [isDirty, scopeKey, settlePending]);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!dirtyRef.current || !scopeKeyRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  useEffect(
    () => () => {
      const pending = pendingRef.current;
      pendingRef.current = null;
      pending?.resolve(false);
    },
    []
  );

  return {
    confirmOpen: Boolean(confirmation && isDirty && scopeKey && confirmation.scopeKey === scopeKey),
    pendingReason: confirmation?.reason || null,
    requestExit,
    confirmExit,
    keepEditing,
    discardAndContinue,
  };
};

export default useUnsavedPricingGuard;
